import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Warning, CheckCircle, ChartLine, CurrencyDollar, GraduationCap } from "@phosphor-icons/react";

export default function ParentDashboard() {
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [perf, setPerf] = useState([]);

  useEffect(() => {
    api.get("/students").then(r => setStudents(r.data)).catch(() => {});
    api.get("/payments").then(r => setPayments(r.data)).catch(() => {});
    api.get("/performance").then(r => setPerf(r.data)).catch(() => {});
  }, []);

  const child = students[0];
  const isInad = child?.payment_status === "inadimplente";
  const avg = perf.length ? (perf.reduce((s, p) => s + (p.score / (p.max_score || 100)) * 100, 0) / perf.length).toFixed(0) : "—";

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl space-y-6 sm:space-y-8">
      <header className="fade-up">
        <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
          <span className="text-[#1E5FFF] font-black">✦</span> Painel do responsável
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">Acompanhamento</h1>
      </header>

      {child && (
        <div data-testid="child-info" className="card-soft p-6 flex items-center gap-5 fade-up">
          <div className="w-16 h-16 rounded-2xl gradient-warm flex items-center justify-center flex-shrink-0 shadow-lg">
            <GraduationCap size={28} color="white" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Aluno vinculado</div>
            <h2 className="font-heading text-2xl font-black text-[#12143A] mt-1">{child.name}</h2>
            <div className="text-sm text-zinc-500">{child.email}</div>
          </div>
        </div>
      )}

      <div className={`card-elevated p-7 fade-up ${isInad ? "border-l-8 border-[#FF3D71]" : "border-l-8 border-[#12B76A]"}`}
           data-testid="payment-status-banner">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isInad ? "bg-[#FFE9EE]" : "bg-[#EAF7EF]"}`}>
            {isInad ? <Warning size={26} weight="duotone" color="#FF3D71" /> : <CheckCircle size={26} weight="duotone" color="#12B76A" />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Status financeiro</div>
            <div className={`font-heading text-3xl font-black mt-1 ${isInad ? "text-[#FF3D71]" : "text-[#12B76A]"}`}>
              {isInad ? "Inadimplente" : "Adimplente"}
            </div>
            <p className="text-sm text-zinc-600 mt-1">
              {isInad ? "Existem pagamentos em atraso. Acesso do aluno pode estar restrito." : "Todos os pagamentos em dia. Acesso liberado."}
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 fade-up">
        <StatCard icon={ChartLine} tint="#1E5FFF" bg="gradient-sky" label="Média geral" value={avg + (avg !== "—" ? "%" : "")} />
        <StatCard icon={GraduationCap} tint="#FF3D71" bg="gradient-blush" label="Notas registradas" value={String(perf.length)} />
        <StatCard icon={CurrencyDollar} tint="#12B76A" bg="bg-[#EAF7EF]" label="Pagamentos" value={String(payments.length)} />
      </div>

      <div className="card-soft overflow-hidden fade-up">
        <div className="p-4 sm:p-6 border-b border-zinc-100">
          <h3 className="font-heading text-lg sm:text-xl font-black">Histórico de pagamentos</h3>
        </div>
        {payments.length === 0 && <p className="text-sm text-zinc-400 py-12 text-center">Nenhum pagamento registrado.</p>}
        {payments.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100 bg-zinc-50/50">
              <th className="py-4 px-6 font-bold">Mês</th><th className="font-bold">Vencimento</th><th className="font-bold">Valor</th><th className="pr-6 font-bold">Status</th>
            </tr></thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#12143A]">{p.month_ref}</td>
                  <td className="font-mono-num text-zinc-500">{p.due_date}</td>
                  <td className="font-mono-num font-bold">R$ {p.amount.toFixed(2)}</td>
                  <td className="pr-6">
                    <span className={`pill-badge ${
                      p.status === "paid" ? "bg-[#EAF7EF] text-[#12B76A]" :
                      p.status === "overdue" ? "bg-[#FFE9EE] text-[#FF3D71]" :
                      "bg-[#FFF6EA] text-[#F79009]"
                    }`}>{p.status.toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, tint, bg, label, value }) {
  return (
    <div className="card-soft p-6 relative overflow-hidden">
      <div className={`absolute inset-0 opacity-40 ${bg}`} />
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
          <Icon size={20} weight="duotone" color={tint} />
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-mono-num text-4xl font-black mt-1 text-[#12143A]">{value}</div>
      </div>
    </div>
  );
}
